// Console motion, motion 12 (the renamed Framer Motion, ADR 0002). Tier 1
// per JEV motion.intensity: one entrance per region, played once on mount,
// and the exit of a dismissed notice. Regions (panels, the stat row,
// notices) fade only: they hold 44px controls, and a fractional translate
// mid-entrance would measure them a hair under 44. A new chat message adds
// a 4px rise, since it holds no control and the rise says "this arrived".
// Nothing loops, nothing that carries meaning (a value, a bar, a verdict)
// moves on its own. Under prefers-reduced-motion every entrance and exit collapses
// to an opacity crossfade of 150ms.
//
// The numbers mirror tokens.css (--dur-200, --dur-200-exit, --dur-150,
// --ease-standard); motion needs them in seconds, so they live here once.
import { useReducedMotion, type Transition } from "motion/react";

/** --ease-standard, the one curve. */
export const EASE_STANDARD = [0.24, 1, 0.4, 1] as const;

/** --dur-200 entrance on the one curve. */
export const enterTransition: Transition = { duration: 0.2, ease: EASE_STANDARD };
/** --dur-200-exit, about 70% of the entrance. */
export const exitTransition: Transition = { duration: 0.14, ease: EASE_STANDARD };
/** --dur-reduced crossfade ceiling. */
export const reducedTransition: Transition = { duration: 0.15, ease: EASE_STANDARD };

/** The rise every console entrance uses, in px (on the 4px grid). */
export const RISE = 4;

export interface EnterMotion {
  initial: { opacity: number; y?: number };
  animate: { opacity: number; y?: number; transition: Transition };
  exit: { opacity: number; y?: number; transition: Transition };
}

/**
 * Props for a motion element that enters once on mount and leaves on
 * removal. Spread onto any `motion.*` element. `rise` adds the 4px rise;
 * keep it off for anything that contains a control.
 */
export function useEnterMotion({ rise = false }: { rise?: boolean } = {}): EnterMotion {
  const reduced = useReducedMotion() === true;
  if (reduced) {
    return {
      initial: { opacity: 0 },
      animate: { opacity: 1, transition: reducedTransition },
      exit: { opacity: 0, transition: reducedTransition },
    };
  }
  if (!rise) {
    return {
      initial: { opacity: 0 },
      animate: { opacity: 1, transition: enterTransition },
      exit: { opacity: 0, transition: exitTransition },
    };
  }
  return {
    initial: { opacity: 0, y: RISE },
    animate: { opacity: 1, y: 0, transition: enterTransition },
    exit: { opacity: 0, y: RISE, transition: exitTransition },
  };
}
